import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import type { AvailableReward } from 'api/recoveryAi.types'
import palette from 'constants/palette'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { StyleObject, useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'
import { generateId } from 'utilities/uniqueId'
import { resolveRewardValueCents } from '../rewardValue'

// Listbox drop-shadow. No theme/palette shadow token exists, so the raw value is
// hoisted to a named constant (CLAUDE.md: extract hardcoded values).
const DROPDOWN_SHADOW = '0 4px 8px rgba(0,0,0,0.1)'

// Selected chips shown before the "View N more" toggle collapses the rest (Figma
// parity — mirrors the ladder's INITIAL_VISIBLE_ROWS).
const INITIAL_VISIBLE_CHIPS = 3

// The two field labels ("Select rewards", "Selected rewards") are visually hidden
// (Figma shows neither) but kept in the DOM so they remain the combobox / chip-list
// accessible names (aria-labelledby). Standard sr-only clip pattern.
const srOnlyStyle: StyleObject = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: '0',
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: '0',
}

// overrides.BaseButton REPLACES uikit's minimal styling entirely, so every
// state must be restated — :hover:enabled needs that exact :enabled qualifier
// to outrank uikit's own same-specificity default, else it renders black.
// belizeHole10 matches the hover tint used elsewhere for minimal-kind links
// (ButtonPanel.tsx, CustomDateRangeFilter.tsx). Mirrors MakeGoodLadder's
// TEXT_LINK_BUTTON_OVERRIDES.
const TEXT_LINK_BUTTON_OVERRIDES = {
  BaseButton: {
    style: {
      fontWeight: 500,
      color: palette.belizeHole,
      backgroundColor: 'transparent',
      paddingLeft: '0',
      paddingRight: '0',
      paddingTop: '0',
      paddingBottom: '0',
      ':hover:enabled': { backgroundColor: palette.belizeHole10 },
      ':active:enabled': { backgroundColor: palette.belizeHole10 },
    },
  },
}

/**
 * Background-color precedence for a listbox option, **selected-wins**: the
 * selected blue tint must never be masked when the option is also keyboard-
 * focused — notably the first option, which is focused on open, so clicking it
 * would otherwise show no color change. Focus stays indicated by the outline.
 * This is the bug fix for the wizard card's pre-extraction focused-wins order.
 *
 * Kept on merchantTheme (not palette) for consistency with the
 * belizeHole10 reference used alongside it here.
 */
export const resolveOptionBackground = (
  isSelected: boolean,
  isFocused: boolean
): string => {
  if (isSelected) return merchantTheme.colors.belizeHole10
  if (isFocused) return merchantTheme.colors.grey05
  return 'white'
}

export interface RewardMultiSelectLabels {
  /** Bold label above the trigger, e.g. "Select rewards". Also the listbox aria-label. */
  selectRewards: string
  /**
   * Already-count-resolved trigger text, e.g. "3 rewards selected" / "1 reward
   * selected". The CONSUMER picks the _one/_other form by count — react-i18nify
   * does not auto-pluralize, so this component never calls `t`.
   */
  rewardsSelected: string
  /** Uppercase section label above the selected-chip list, e.g. "Selected rewards". */
  selectedRewardsLabel: string
  /**
   * aria-label builder for a chip's remove button, e.g. name => `Remove ${name}`.
   * A function so the per-name %{name} interpolation happens in the labels helper
   * (recovery_ai.reward_picker.remove_reward_aria_label), not in this component.
   */
  // rewardLabel is nullable (AvailableReward.label): the builder supplies the
  // "this reward" stand-in so the button always has an accessible name.
  removeRewardAriaLabel: (rewardLabel: string | null) => string
  /** Empty-state sentence, e.g. "No rewards available to select yet." */
  noRewardsAvailable: string
  /** Empty-state CTA link text, e.g. "Create a reward". */
  createRewardCta: string
  /**
   * Selected-chip collapse toggle text, e.g. count => `View ${count} more rewards`.
   * A builder so the per-count %{count} plural form is picked in the labels helper
   * (recovery_ai.reward_picker.view_more_one/other), not in this component.
   */
  viewMoreRewards: (count: number) => string
  /** Selected-chip collapse expanded-state text, e.g. "Show less". */
  showLessRewards: string
  /**
   * Formats a reward's GUEST VALUE (never COGS) in cents as display copy,
   * e.g. cents => `$4.50 value`. A builder — like removeRewardAriaLabel and
   * viewMoreRewards above — so the shared `recovery_ai.reward_value` string
   * (see rewardValue.ts) is resolved in the labels helper, keeping this
   * component i18n-agnostic. The caller resolves which cents field to pass
   * via `resolveRewardValueCents` (value_cents, falling back to cost_cents).
   */
  rewardValue: (cents: number) => string
}

export interface RewardMultiSelectProps {
  /** ACTIVE-filtered rewards only. Consumer filters (inactive rewards can't be issued). */
  rewards: AvailableReward[]
  /** Controlled selection. Order-insensitive; consumer owns it. */
  selectedIds: string[]
  /** Toggle a reward in/out of the selection. */
  onToggle: (id: string) => void
  /** Remove a reward from the selection (chip "×"). */
  onRemove: (id: string) => void
  /** Pre-translated, namespace-owned strings (the component is i18n-agnostic). */
  labels: RewardMultiSelectLabels
  /** Optional stable id prefix for tests; defaults to a generateId()-based id. */
  idPrefix?: string
  /**
   * Read-only mode (AI-64 CASL gate): the host has `access` but not `manage` on
   * RecoveryAI. The trigger and every chip "×" go disabled, and the keyboard
   * contract stops opening/toggling — the current selection still renders, so
   * the merchant can read what is approved. Defaults false so the wizard and
   * the Agent-Tuning modal call sites are unchanged.
   */
  disabled?: boolean
}

/**
 * Controlled, presentational reward multi-select listbox (AI-91). Extracted from
 * the Settings `ApprovedRewardsPane` and the wizard `ApprovedRewardsCard`, which
 * had drifted nearly-duplicate copies. State + persistence stay in the consumers;
 * this component owns the trigger/dropdown markup, keyboard nav, outside-click,
 * scroll-into-view, the empty state, the selected-chip list, and ALL a11y wiring
 * (per-option ids + aria-activedescendant + aria-controls + listbox/option roles)
 * so the two surfaces can never diverge again.
 *
 * NOT used by the AI-119 Grant Reward modal — that is single-select, a separate
 * widget. Do not generalize this component to support single-select.
 */
const RewardMultiSelect = ({
  rewards,
  selectedIds,
  onToggle,
  onRemove,
  labels,
  idPrefix,
  disabled = false,
}: RewardMultiSelectProps) => {
  const [css] = useStyletron()
  const [isOpen, setIsOpen] = useState(false)
  const [focusedIndex, setFocusedIndex] = useState(-1)
  // Selected-chip collapse (Figma parity): show the first few, expand on demand.
  const [chipsExpanded, setChipsExpanded] = useState(false)
  // Generated once per mount so the trigger's aria-controls points at the
  // listbox's id and each option carries a stable per-render id (full WAI-ARIA
  // combobox/listbox association).
  const [listboxId] = useState(
    () => `${idPrefix ?? `reward-multiselect-${generateId()}`}-listbox`
  )
  // With role="combobox" (round-2 Finding 1), this is a select-only combobox per
  // the WAI-ARIA APG: its accessible NAME is the static "Select rewards" field
  // label (labelId via aria-labelledby), and its VALUE is the trigger's own text
  // content ("N rewards selected"). That value is exposed/announced separately
  // from the name, so the name is never count-only (the round-1 Finding-5 goal)
  // WITHOUT a self-reference — a combobox does not fold its own value into its
  // name the way name-from-content does on a plain button, so the prior
  // `${labelId} ${triggerId}` self-ref would (and did) collapse to label-only.
  // Derived from listboxId so all ids share one stable per-mount prefix.
  const labelId = `${listboxId}-label`
  const triggerId = `${listboxId}-trigger`
  const optionId = useCallback(
    (index: number) => `${listboxId}-option-${index}`,
    [listboxId]
  )
  const dropdownRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  // The trigger button, so focus can be returned to it when the listbox closes
  // via Escape or an outside click (WCAG 3.2.1 — focus must not be lost to the
  // body, which would strand keyboard/AT users at the top of the document).
  const triggerRef = useRef<HTMLButtonElement>(null)

  // Pre-compute a Set of selected ids so membership checks in the option map and
  // the selected-chip filter are O(1) instead of O(selectedIds) per reward
  // (CLAUDE.md: pre-compute Set objects outside .some()/.filter() loops).
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds])
  const selectedRewards = useMemo(
    () => rewards.filter(r => selectedIdSet.has(r.id)),
    [rewards, selectedIdSet]
  )
  // Collapse the selected-chip list to the first few (Figma parity); the rest
  // reveal via the "View N more" toggle rendered below the list.
  const hiddenChipCount = selectedRewards.length - INITIAL_VISIBLE_CHIPS
  const shownChips = chipsExpanded
    ? selectedRewards
    : selectedRewards.slice(0, INITIAL_VISIBLE_CHIPS)

  // Outside click to close.
  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false)
        setFocusedIndex(-1)
        // Return focus to the trigger so a keyboard/AT user lands back on the
        // control they dismissed rather than on document.body (WCAG 3.2.1).
        triggerRef.current?.focus()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  // Keyboard navigation. Space (' ') always calls preventDefault so the listbox
  // doesn't scroll the page on toggle/open.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Read-only: the trigger carries `disabled` (so it isn't focusable), but
      // this handler sits on the wrapper — guard it too so no stray key event
      // can open the listbox or toggle a reward.
      if (disabled) return
      if (!isOpen) {
        if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          setIsOpen(true)
          setFocusedIndex(0)
        }
        return
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setFocusedIndex(prev => Math.min(prev + 1, rewards.length - 1))
          break
        case 'ArrowUp':
          e.preventDefault()
          setFocusedIndex(prev => Math.max(prev - 1, 0))
          break
        case 'Enter':
        case ' ':
          e.preventDefault()
          if (focusedIndex >= 0 && focusedIndex < rewards.length) {
            onToggle(rewards[focusedIndex].id)
          }
          break
        case 'Escape':
          e.preventDefault()
          setIsOpen(false)
          setFocusedIndex(-1)
          // Return focus to the trigger (WCAG 3.2.1) — Escape closing the
          // listbox must not strand focus on the now-removed option/body.
          triggerRef.current?.focus()
          break
      }
    },
    [disabled, isOpen, focusedIndex, rewards, onToggle]
  )

  // Tab-close (Finding 2). React's onBlur is the bubbling focusout, so blurring
  // the trigger fires it on the wrapper. Because the options are non-focusable
  // (role="option" divs with no tabindex), Tab from the trigger moves focus OUT
  // of the dropdown entirely → relatedTarget is outside dropdownRef → close.
  // Deliberately does NOT return focus to the trigger: Tab must move forward
  // naturally. Escape/outside-click keep their own focus-return (WCAG 3.2.1).
  // useCallback (CLAUDE.md: hooks returning callbacks must be memoized; the
  // sibling handleKeyDown already is). Deps [] — it only reads the stable
  // dropdownRef and the stable state setters.
  const handleBlur = useCallback((e: React.FocusEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(e.relatedTarget as Node)
    ) {
      setIsOpen(false)
      setFocusedIndex(-1)
    }
  }, [])

  // Scroll the focused option into view. Look the node up directly by its stable
  // per-option id rather than querying every option and indexing — O(1) instead
  // of materializing the whole option NodeList on each focusedIndex change.
  useEffect(() => {
    if (focusedIndex >= 0) {
      document
        .getElementById(optionId(focusedIndex))
        ?.scrollIntoView({ block: 'nearest' })
    }
  }, [focusedIndex, optionId])

  // With no active rewards to choose from, the dropdown would just read
  // "0 rewards selected" with nothing to pick — so point the merchant to where
  // rewards are created. New tab keeps the host surface intact.
  if (rewards.length === 0) {
    return (
      <Text.BodyText4 color="grey70">
        {labels.noRewardsAvailable}{' '}
        <Link
          to="/rewards"
          target="_blank"
          rel="noopener noreferrer"
          className={css({ color: palette.belizeHole })}
        >
          {labels.createRewardCta}
        </Link>
      </Text.BodyText4>
    )
  }

  return (
    <>
      <div className="d-flex flex-column">
        {/* Visually hidden (Figma shows no field label) but kept in the DOM so
            the trigger's aria-labelledby still resolves to a non-count accessible
            name ("Select rewards"). */}
        <span id={labelId} className={css(srOnlyStyle)}>
          {labels.selectRewards}
        </span>
        <div
          ref={dropdownRef}
          className={css({ position: 'relative', marginTop: '8px' })}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
        >
          {/* Intentionally a native <button>, not @thanx/uikit <Button> (R11):
              this is a WAI-ARIA combobox/listbox TRIGGER, not a CTA. It must
              read as a neutral form field (full-width, grey30 border, white bg,
              left-aligned text + right chevron) and carry hand-wired listbox
              ARIA (haspopup/expanded/controls/activedescendant). The uikit
              Button bakes in kind-driven CTA chrome + a baseui root structure
              that we'd have to fight via $style overrides for zero a11y gain —
              and the sibling RecoveryAI TabBar trigger is native for the same
              reason. The a11y here is complete and explicit, so the design
              system's defaults add nothing. */}
          <button
            ref={triggerRef}
            id={triggerId}
            type="button"
            // role="combobox" makes this the valid select-only combobox owner
            // (WAI-ARIA APG): aria-activedescendant is spec-invalid on
            // role="button", so without this the arrow-key virtual focus is
            // never announced. The other listbox-owner ARIA
            // (expanded/haspopup/controls/activedescendant) is wired below.
            role="combobox"
            disabled={disabled}
            onClick={() => {
              // Every close path resets focusedIndex to -1 (Escape, Tab-blur,
              // outside-click); click-CLOSE must too, or it leaves a stale
              // virtual cursor behind.
              if (isOpen) {
                setIsOpen(false)
                setFocusedIndex(-1)
              } else {
                setIsOpen(true)
                setFocusedIndex(0)
              }
            }}
            className={css({
              width: '100%',
              padding: '10px 16px',
              // grey30 (border): kept on merchantTheme for visual parity with the
              // pre-extraction pickers. Text is palette.grey70 below.
              border: `1px solid ${merchantTheme.colors.grey30}`,
              borderRadius: '4px',
              backgroundColor: disabled ? merchantTheme.colors.grey05 : 'white',
              cursor: disabled ? 'not-allowed' : 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              textAlign: 'left',
              fontFamily: 'Lato, sans-serif',
              fontSize: '16px',
              // Pinned in both states: the UA's disabled grey would drop the
              // "N rewards selected" value below WCAG 1.4.3 on grey05.
              color: palette.grey70,
            })}
            // Labelled by the static "Select rewards" field label only: a
            // select-only combobox's NAME is the label and its VALUE is the
            // text content ("N rewards selected"), so the name is never
            // count-only (Finding 5) without self-referencing triggerId — which
            // a combobox would not fold into its name anyway.
            aria-labelledby={labelId}
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            // Unconditional: the listbox is always in the DOM (Finding 4), so the
            // control it owns never changes. aria-expanded carries open/closed.
            aria-controls={listboxId}
            aria-activedescendant={
              isOpen && focusedIndex >= 0 ? optionId(focusedIndex) : undefined
            }
          >
            {labels.rewardsSelected}
            {/* Filled caret (Figma parity) — a solid triangle, not the outline
                chevron; grey90 so it reads as the dark marker in the mock. */}
            <i
              className={`fa fa-caret-${isOpen ? 'up' : 'down'} ${css({
                color: palette.grey90,
              })}`}
              aria-hidden="true"
            />
          </button>

          {/* Always rendered (Finding 4): hidden via the `hidden` attribute +
              display:none when closed, so aria-controls can point at a node that
              always exists and the scrollIntoView effect can find options by id.
              The `hidden` attribute removes the listbox (and its options) from
              the a11y tree when closed. */}
          <div
            ref={listRef}
            hidden={!isOpen}
            className={css({
              display: isOpen ? 'block' : 'none',
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              border: `1px solid ${merchantTheme.colors.grey30}`,
              borderRadius: '4px',
              backgroundColor: 'white',
              boxShadow: DROPDOWN_SHADOW,
              zIndex: 10,
              maxHeight: '240px',
              overflowY: 'auto',
            })}
            id={listboxId}
            role="listbox"
            aria-label={labels.selectRewards}
            aria-multiselectable="true"
          >
            {rewards.map((reward, index) => {
              const isSelected = selectedIdSet.has(reward.id)
              const isFocused = index === focusedIndex
              return (
                // A plain <div role="option"> with NO tabindex (Findings 1 + 3):
                // it is not focusable, so clicking it cannot move DOM focus off
                // the trigger — the virtual cursor (focusedIndex) and DOM focus
                // can never diverge, which is what made Space/Enter toggle the
                // wrong reward. onClick syncs the cursor to the clicked option so
                // mouse and keyboard stay consistent. A native <button> here also
                // conflicted with role="option" (button + option is invalid ARIA).
                <div
                  key={reward.id}
                  id={optionId(index)}
                  role="option"
                  aria-selected={isSelected}
                  // Stop the option from stealing focus on press. The option is a
                  // non-focusable div, so a mouse-down would blur the trigger →
                  // relatedTarget is null (focus heads to <body>) → handleBlur
                  // fires and closes the listbox on mouse-DOWN, removing the
                  // option before the click's onClick can toggle (the reported
                  // "clicking just closes the combo"). Preventing the default
                  // focus shift keeps the trigger focused, so the listbox stays
                  // open and the click below toggles. Keyboard nav is unaffected
                  // (it never moves DOM focus off the trigger).
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => {
                    onToggle(reward.id)
                    setFocusedIndex(index)
                  }}
                  className={css({
                    width: '100%',
                    padding: '10px 16px',
                    // Selected-wins precedence (see resolveOptionBackground).
                    // belizeHole50 (focus outline) also stays on merchantTheme
                    // — palette has no belizeHole50 key.
                    backgroundColor: resolveOptionBackground(
                      isSelected,
                      isFocused
                    ),
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    textAlign: 'left',
                    fontFamily: 'Lato, sans-serif',
                    fontSize: '14px',
                    outline: isFocused
                      ? `2px solid ${merchantTheme.colors.belizeHole50}`
                      : 'none',
                    outlineOffset: '-2px',
                  })}
                >
                  <span>{reward.label}</span>
                  {/* palette.grey70 (#6A747F, ~4.6:1), not grey50: the value
                      text needs WCAG 1.4.3 AA contrast (4.5:1), which grey50
                      (~2.3:1) fails. */}
                  <span className={css({ color: palette.grey70 })}>
                    {labels.rewardValue(resolveRewardValueCents(reward))}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {selectedRewards.length > 0 && (
        <div className={`d-flex flex-column ${css({ marginTop: '16px' })}`}>
          {/* id so the chip list can programmatically reference this label via
              aria-labelledby (WCAG 1.3.1). Visually hidden (Figma shows no
              "Selected rewards" heading) but kept in the DOM as the list's name. */}
          <span id={`${listboxId}-selected-label`} className={css(srOnlyStyle)}>
            {labels.selectedRewardsLabel}
          </span>
          <div
            // The "Selected rewards" span (sr-only above) is the list's
            // programmatic name via this association (WCAG 1.3.1).
            aria-labelledby={`${listboxId}-selected-label`}
            className={`d-flex flex-column ${css({ gap: '8px' })}`}
          >
            {shownChips.map(reward => (
              <div
                key={reward.id}
                className={`d-flex align-items-center justify-content-between ${css(
                  {
                    padding: '12px 16px',
                    // Each chip is its own bordered, rounded card separated by an
                    // 8px gap (Figma parity — replaces the single container with
                    // hairline dividers), mirroring the make-good ladder rows.
                    border: `1px solid ${merchantTheme.colors.grey20}`,
                    borderRadius: '6px',
                  }
                )}`}
              >
                <Text.BodyText4>{reward.label}</Text.BodyText4>
                <div
                  className={`d-flex align-items-center ${css({
                    gap: '12px',
                  })}`}
                >
                  <Text.BodyText4 color="grey70">
                    {labels.rewardValue(resolveRewardValueCents(reward))}
                  </Text.BodyText4>
                  {/* Native <button>, not @thanx/uikit <Button kind="minimal">,
                      for the same reason the trigger above is (R11): uikit's
                      getSizeStyle makes even the SMALL Button too tall for a
                      chip row, and shrinking it via `overrides.BaseButton`
                      REPLACES (uikit spreads `...overrides` last) uikit's own
                      minimal bg/color — collapsing it to baseui's
                      filled-primary default — so we'd have to re-hand-roll the
                      minimal look for zero gain. The control is already fully
                      a11y-correct (type=button, aria-label, aria-hidden icon).

                        context: this used to cite a sibling remove-× on the
                        wizard's old escalation-routing card, which D1 deleted.
                        Every other RecoveryAI remove-× is a uikit Button now,
                        so this is the last hand-rolled one — deliberate, not
                        drift. */}
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => onRemove(reward.id)}
                    className={css({
                      border: 'none',
                      background: 'none',
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      // Figma-parity color: grey40, lighter than the WCAG-passing
                      // grey70 previously used — an accepted trade-off (like the
                      // AI-recommended pill recolor) to match the design exactly.
                      color: palette.grey40,
                      // 4px 8px around the 16px icon ≈ 24×32px, clearing the
                      // WCAG 2.5.8 (AA) 24×24 minimum touch target.
                      fontSize: '16px',
                      padding: '4px 8px',
                    })}
                    aria-label={labels.removeRewardAriaLabel(reward.label)}
                  >
                    <i className="fa fa-times" aria-hidden="true" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {hiddenChipCount > 0 && (
            <div className="mt-s">
              {/* Non-bold flush link toggle (see TEXT_LINK_BUTTON_OVERRIDES);
                  aria-expanded is forwarded to the real <button> for AT. */}
              <Button
                kind="minimal"
                onClick={() => setChipsExpanded(prev => !prev)}
                aria-expanded={chipsExpanded}
                overrides={TEXT_LINK_BUTTON_OVERRIDES}
              >
                {chipsExpanded
                  ? labels.showLessRewards
                  : labels.viewMoreRewards(hiddenChipCount)}
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  )
}

export default RewardMultiSelect
